// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.
import createUrlRegExp from 'url-regex-safe';

import {splitMessageBasedOnCaretPosition, splitMessageBasedOnCaretPositionWithoutSelection} from 'utils/post_utils';
import {getTeamURL} from 'utils/url';
import {getSelectedText} from 'utils/utils';

export function parseTable(html: string): HTMLTableElement | null {
    return new DOMParser().parseFromString(html, 'text/html').querySelector('table');
}

export function getTable(clipboardData: DataTransfer): HTMLTableElement | null {
    if (Array.from(clipboardData.types).indexOf('text/html') === -1) {
        return null;
    }

    const html = clipboardData.getData('text/html');

    if (!(/<table/i).test(html)) {
        return null;
    }

    const table = parseTable(html);
    if (!table) {
        return null;
    }

    return table;
}

export function getPlainText(clipboardData: DataTransfer): string | false {
    if (Array.from(clipboardData.types).indexOf('text/plain') === -1) {
        return false;
    }

    const plainText = clipboardData.getData('text/plain');

    return plainText;
}

export function getChannelNameFromURL(text: string): string {
    const teamURL = getTeamURL();
    const prefix = teamURL + '/channels/';
    if (text.trim().indexOf(prefix) === 0) {
        return text.trim().substr(prefix.length);
    }
    return '';
}

export function getUserNameFromURL(text: string): string {
    const teamURL = getTeamURL();
    const prefix = teamURL + '/messages/@';
    if (text.trim().indexOf(prefix) === 0) {
        return text.trim().substr(prefix.length);
    }
    return '';
}

export function isGitHubCodeBlock(tableClassName: string): boolean {
    const result = (/\b(js|blob|diff)-./).test(tableClassName);
    return result;
}

function columnText(column: Element): string {
    const noBreakSpace = '\u00A0';
    const text =
        column.textContent == null ? noBreakSpace : column.textContent.trim().replace(/\|/g, '\\|').replace(/\n/g, ' ');
    return text;
}

function tableHeaders(row: HTMLTableRowElement): string[] {
    return Array.from(row.querySelectorAll('td, th')).map(columnText);
}

export function formatMarkdownTableMessage(table: HTMLTableElement, message?: string, caretPosition?: number): string {
    const rows = Array.from(table.querySelectorAll('tr'));

    const headerRow = rows.shift();
    const headers = headerRow ? tableHeaders(headerRow) : [];
    const spacers = headers.map(() => '---');
    const header = `|${headers.join(' | ')}|\n|${spacers.join(' | ')}|\n`;

    const body = rows.
        map((row) => {
            return `|${Array.from(row.querySelectorAll('td')).map(columnText).join(' | ')}|`;
        }).
        join('\n');

    const formattedTable = `\n${header}${body}\n`;
    if (!message) {
        return formattedTable.trim() + '\n';
    }
    if (typeof caretPosition === 'undefined') {
        return `${message}\n${formattedTable}`;
    }
    const newMessage = [message.slice(0, caretPosition).trim(), formattedTable, message.slice(caretPosition).trim()];
    return newMessage.join('\n').replace(/\n{2,}$/, '\n');
}

export function formatGithubCodePaste(
    caretPosition: number,
    message: string,
    clipboardData: DataTransfer,
): {formattedMessage: string; formattedCodeBlock: string} {
    const {firstPiece, lastPiece} = splitMessageBasedOnCaretPosition(caretPosition, message);

    // Add new lines if content exists before or after the cursor.
    const requireStartLF = firstPiece === '' ? '' : '\n';
    const requireEndLF = lastPiece === '' ? '' : '\n';
    const formattedCodeBlock = requireStartLF + '```\n' + getPlainText(clipboardData) + '\n```' + requireEndLF;
    const formattedMessage = `${firstPiece}${formattedCodeBlock}${lastPiece}`;

    return {formattedMessage, formattedCodeBlock};
}

export function urlRegex() {
    return createUrlRegExp({exact: true, localhost: false, ipv4: false, ipv6: false, strict: true});
}

export function formatLink(
    clipboardData: DataTransfer,
    message: string,
    caretPosition: number,
): {newMessage: string; newCaretPosition: number} | null {
    if (!clipboardData) {
        return null;
    }

    const text = getPlainText(clipboardData);
    if (!text) {
        return null;
    }

    const urlRe = urlRegex();
    if (!urlRe.test(text)) {
        return null;
    }

    const selectedText = getSelectedText();

    let paste = '';
    let formatted = false;

    const channelName = getChannelNameFromURL(text);
    if (channelName) {
        paste = '~' + channelName;
        formatted = true;
    }

    const userName = getUserNameFromURL(text);
    if (userName) {
        paste = '@' + userName;
        formatted = true;
    }

    if (!channelName && !userName && selectedText) {
        paste = `[${selectedText}](${text})`;
    }

    if (!formatted && selectedText && urlRe.test(selectedText)) {
        return null;
    }

    if (!paste) {
        return null;
    }

    const splitter = selectedText ? splitMessageBasedOnCaretPositionWithoutSelection : splitMessageBasedOnCaretPosition;
    const {firstPiece, lastPiece} = splitter(caretPosition, message);

    if (firstPiece && firstPiece[firstPiece.length - 1] !== ' ') {
        paste = ' ' + paste;
    }
    if (lastPiece && lastPiece !== selectedText && lastPiece[0] !== ' ') {
        paste += ' ';
    }

    return {
        newMessage: `${firstPiece}${paste}${lastPiece}`,
        newCaretPosition: firstPiece.length + paste.length,
    };
}
